import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'system';
type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  ...props
}: ThemeProviderProps) {
  // Get the saved theme from localStorage or use system preference
  const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem(storageKey) as Theme;
    return saved || 'system';
  };

  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = getInitialTheme();
    if (savedTheme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme === 'dark' ? 'dark' : 'light';
  });

  const setTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setThemeState(newTheme);
  }, [storageKey]);

  // Update theme when system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      // Force a re-render by updating the theme state
      setThemeState(prevTheme => {
        // If we're using system theme, update the resolved theme
        if (prevTheme === 'system') {
          const newTheme = mediaQuery.matches ? 'dark' : 'light';
          // console.log('System theme changed, applying:', newTheme);
          document.documentElement.classList.remove('light', 'dark');
          document.documentElement.classList.add(newTheme);
          document.documentElement.style.colorScheme = newTheme;
          document.body.style.backgroundColor = newTheme === 'dark' ? '#0f172a' : '#ffffff';
          setResolvedTheme(newTheme);
        }
        return prevTheme;
      });
    };

    // Set up the listener
    mediaQuery.addEventListener('change', handleChange);

    // Clean up
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme when component mounts or theme changes
  useEffect(() => {
    const root = document.documentElement;

    // Remove all theme classes first
    root.classList.remove('light', 'dark');

    // Determine the actual theme to apply
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldUseDark = theme === 'system' ? systemPrefersDark : theme === 'dark';
    const themeToApply = shouldUseDark ? 'dark' : 'light';

    // console.log(`Applying theme - Selected: ${theme}, Actual: ${themeToApply}, System dark: ${systemPrefersDark}`);

    // Apply the theme
    root.classList.add(themeToApply);
    root.style.colorScheme = themeToApply;
    document.body.style.backgroundColor = shouldUseDark ? '#0f172a' : '#ffffff';

    // Update the resolved theme
    setResolvedTheme(themeToApply);

    // Save to localStorage if not already set or different
    if (theme !== localStorage.getItem(storageKey)) {
      localStorage.setItem(storageKey, theme);
      // console.log('Theme saved to localStorage:', { storageKey, theme });
    }
  }, [theme, storageKey]);

  const value = {
    theme,
    resolvedTheme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
